import { thunkActionLoading } from '@configs'
import { useAppDispatch, useAppSelector } from '@redux'
import { message, TableProps } from 'antd'
import { t } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { useDebounce } from 'src/hooks/useDebounce'
import { ICartItem, IQuery } from 'src/interfaces/cart-management'

import { getListCartAction } from 'src/redux/actions/cart-management'
import { getListWorkshopEventAction } from 'src/redux/actions/workshopEvent'

interface IPageInfo {
  currentPage: number
  total?: number
}

export const useListCartsTable = () => {
  const lastParams = useRef<IQuery>()
  const dispatch = useAppDispatch()

  const [listEventOption, setListEventOption] = useState<any>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
  })
  const [eventSearchString, setEventSearchString] = useState('')
  const searchEventDebounce = useDebounce<string | null>(eventSearchString, 800)

  const [eventGuid, setEventGuid] = useState<string | undefined>()
  const [searchString, setSearchString] = useState<string>('')
  const debouncedValue = useDebounce<string | null>(searchString, 800)

  const carts = useAppSelector((state) => state.carts.carts)

  const isGettingCarts = useAppSelector(
    (state) => state.carts.loadings[thunkActionLoading.GET_LIST_LOADING]
  )

  const isGettingEvent = useAppSelector(
    (state) =>
      state.workshopEvents.loadings[thunkActionLoading.GET_WORKSHOP_EVENT]
  )

  const getListCarts = async (params: IQuery) => {
    if (
      JSON.stringify(params) === JSON.stringify(lastParams.current) &&
      !eventGuid
    )
      return
    lastParams.current = params
    try {
      await dispatch(getListCartAction(params)).unwrap()
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      !isGettingEvent &&
      pageInfo.total &&
      listEventOption.length < pageInfo?.total
    ) {
      getListEvents(
        { page: pageInfo.currentPage + 1, search: eventSearchString },
        false
      )
    }
  }

  const getListEvents = async (params: IQuery, isSearch: boolean) => {
    try {
      const res = await dispatch(
        getListWorkshopEventAction({
          ...params,
          isPublished: true,
          status: 'upcoming',
        })
      ).unwrap()
      if (res.success) {
        const data = res.data.items.map((item) => ({
          label:
            item?.name?.length > 30
              ? `${item?.name?.slice(0, 30)}...`
              : item?.name,
          value: item?.guid,
        }))
        if (isSearch) {
          setListEventOption(data)
        } else {
          setListEventOption((pre: any) => pre.concat(data))
        }
        setPageInfo({ total: res.data.total, currentPage: res.data.page })
      }
    } catch (error: any) {
      message.error({
        content: error?.message ?? t('error:Can not get list Package select'),
      })
    }
  }

  useEffect(() => {
    getListCarts({
      search: searchString?.trim() || undefined,
      eventGuid: eventGuid || undefined,
      page: 1,
      limit: 10,
    })
  }, [eventGuid])

  useEffect(() => {
    getListCarts({
      search: debouncedValue?.trim() || undefined,
      eventGuid: eventGuid || undefined,
      page: 1,
      limit: 10,
    })
  }, [debouncedValue])

  useEffect(() => {
    getListEvents(
      {
        page: 1,
        limit: 10,
        search: searchEventDebounce || '',
      },
      true
    )
  }, [searchEventDebounce])

  const handleClear = () => {
    setSearchString('')
    setEventGuid(undefined)
  }

  const columns: TableProps<ICartItem>['columns'] = [
    {
      dataIndex: 'index',
      align: 'center',
      title: 'No',
      key: 'test',
      render(value, record, index) {
        return <p className="m-0 font-bold">{index + 1}</p>
      },
    },
    {
      dataIndex: 'userId',
      align: 'center',
      title: 'User ID',
      key: 'userId',
      render: (record) => {
        return <p className="m-0 font-bold">{record}</p>
      },
    },
    {
      dataIndex: 'name',
      align: 'center',
      title: 'Username',
      key: 'name',
      // width: '4%',
      render: (record) => {
        return <p className="m-0 font-bold">{record}</p>
      },
    },

    {
      dataIndex: 'email',
      align: 'center',
      title: 'Email',
      key: 'email',
      // width: '4%',
      render: (record) => {
        return <p className="m-0 font-bold">{record}</p>
      },
    },
  ]

  return {
    carts: carts?.length ? carts : [],
    columns,
    searchString,
    setSearchString,
    getListCarts,
    isGettingCarts,
    handleClear,
    eventGuid,
    setEventGuid,
    setEventSearchString,
    listEventOption,
    handleScroll,
    isGettingEvent,
  }
}
